import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { tenantManagementApi, invoicingApi } from './client';

/**
 * Superficie EXTERNA: portal de clientes del estudio (el cliente final del estudio entra acá a
 * ver sus honorarios pendientes y pagarlos). Es un surface separado del admin — NO usa el JWT del
 * AuthContext ni los headers X-Tenant-Id. Se autentica con su propio token (magic link → JWT con
 * scope `client-portal:read` + claims tenant_id + study_client_id), efímero en sessionStorage
 * (se pierde al cerrar la pestaña, requisito de seguridad: el portal es read-only y de bajo
 * privilegio, no merece persistencia tipo "recordarme").
 *
 * Consume dos services:
 *   - TenantManagement (7074): auth (request/redeem), me, branding.
 *   - Invoicing (7080): receivables (honorarios abiertos del cliente, con pay-links).
 *
 * El tenant lo resuelve el backend del claim `tenant_id` del token — por eso NO mandamos
 * X-Tenant-Id. Sólo Authorization Bearer.
 */

const PORTAL_TOKEN_KEY = 'tilde_portal_token';

export function getPortalToken(): string | null {
  try {
    return sessionStorage.getItem(PORTAL_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setPortalToken(token: string | null): void {
  try {
    if (token) {
      sessionStorage.setItem(PORTAL_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(PORTAL_TOKEN_KEY);
    }
  } catch {
    /* sessionStorage no disponible (modo privado estricto) → token sólo en memoria del módulo */
  }
}

const COMMON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

// Reusamos las baseURL ya resueltas por client.ts (respetan env vars y staging público) en vez de
// recomputarlas. Instancias propias para no heredar el interceptor admin (token + X-Tenant-Id).
function portalInstance(baseURL: string | undefined): AxiosInstance {
  const instance = axios.create({ baseURL, timeout: 30000, headers: COMMON_HEADERS });
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getPortalToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });
  return instance;
}

// Exportadas para tests (se mockean .get/.post). No usar desde componentes: usá las funciones.
export const portalTmApi = portalInstance(tenantManagementApi.defaults.baseURL ?? undefined);
export const portalInvoicingApi = portalInstance(invoicingApi.defaults.baseURL ?? undefined);

export interface PortalBranding {
  displayName: string;
  logoUrl: string | null;
  primaryColor: string | null;
  supportEmail: string | null;
}

export interface PortalMe {
  studyClientId: string;
  legalName: string;
  cuit: string;
  tradeName: string | null;
  contactName: string | null;
  branding: PortalBranding | null;
}

export interface PortalReceivable {
  id: string;
  issueDate: string; // DateOnly → "2026-06-18"
  dueDate: string;
  amount: number;
  currency: string;
  daysOverdue: number;
  paymentLinkUrl: string | null;
}

export interface PortalTokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

/** Dispara el envío del magic link al email (si el email pertenece a un cliente del tenant). */
export async function requestMagicLink(tenantId: string, email: string): Promise<void> {
  await portalTmApi.post('/client-portal/auth/request', { tenantId, email });
}

/** Canjea el token del magic link por un JWT de portal. Lo persiste en sessionStorage. */
export async function redeemMagicLink(token: string): Promise<PortalTokenResponse> {
  const { data } = await portalTmApi.post<PortalTokenResponse>('/client-portal/auth/redeem', {
    token,
  });
  setPortalToken(data.accessToken);
  return data;
}

/** Branding del estudio para la pantalla de login (anónimo, por tenantId). */
export async function getPortalBranding(tenantId: string): Promise<PortalBranding> {
  const { data } = await portalTmApi.get<PortalBranding>('/client-portal/branding', {
    params: { tenantId },
  });
  return data;
}

/** Datos del cliente autenticado (incluye branding). Requiere token de portal. */
export async function getPortalMe(): Promise<PortalMe> {
  const { data } = await portalTmApi.get<PortalMe>('/client-portal/me');
  return data;
}

/** Honorarios abiertos del cliente, ordenados por vencimiento, con pay-links. */
export async function getPortalReceivables(): Promise<PortalReceivable[]> {
  const { data } = await portalInvoicingApi.get<PortalReceivable[]>('/client-portal/receivables');
  return data;
}
